import React from 'react';
import { CheckCircle, AlertTriangle, Pause, Play, Edit, Trash2, Info, AlertCircle } from 'lucide-react';
import { formatRelativeTime, formatDateInUserTimezone } from '../utils/timezone';

const MonitorCard = ({ monitor, onEdit, onDelete, onToggleStatus, selected = false, onSelectChange }) => {
  const getStatusColor = () => {
    if (monitor.status !== 'active') return 'gray';
    return monitor.is_up ? 'green' : 'red';
  };

  const getStatusIcon = () => {
    if (monitor.status !== 'active') return Pause;
    return monitor.is_up ? CheckCircle : AlertTriangle;
  };

  const getImpactColor = (userImpact) => {
    switch (userImpact) {
      case 'HIGH':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'MEDIUM':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'LOW':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const getImpactIcon = (userImpact) => {
    switch (userImpact) {
      case 'HIGH':
        return AlertTriangle;
      case 'MEDIUM':
        return AlertCircle;
      case 'LOW':
        return Info;
      default:
        return Info;
    }
  };

  const StatusIcon = getStatusIcon();
  const statusColor = getStatusColor();

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-4 sm:p-6 transition-colors ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 min-w-0 flex-1 mr-3">
          {onSelectChange && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelectChange(monitor.id, e.target.checked)}
              className="mt-2 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
              data-testid={`monitor-select-${monitor.id}`}
              title="Select for bulk actions"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={`p-2 rounded-lg flex-shrink-0 ${
            statusColor === 'green' ? 'bg-green-50 text-green-600' :
            statusColor === 'red' ? 'bg-red-50 text-red-600' :
            'bg-gray-50 text-gray-600'
          }`}>
            <StatusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{monitor.name}</h3>
            <p className="text-xs sm:text-sm text-gray-500 break-all">{monitor.url}</p>
          </div>
        </div>
        
        {/* Mobile: Stack buttons vertically, Desktop: Horizontal */}
        <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2 flex-shrink-0">
          <button
            onClick={() => onToggleStatus(monitor)}
            className={`p-2 rounded-lg transition-colors ${
              monitor.status === 'active' 
                ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
            title={monitor.status === 'active' ? 'Pause monitoring' : 'Resume monitoring'}
          >
            {monitor.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(monitor)}
            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(monitor)}
            className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
        <div>
          <p className="text-gray-500 text-xs sm:text-sm">Status</p>
          <p className={`font-medium text-sm sm:text-base ${
            statusColor === 'green' ? 'text-green-600' :
            statusColor === 'red' ? 'text-red-600' :
            'text-gray-600'
          }`}>
            {monitor.status !== 'active' ? 'Paused' : monitor.is_up ? 'Online' : 'Offline'}
          </p>
        </div>
        <div>
          <p className="text-gray-500 text-xs sm:text-sm">Response</p>
          <p className="font-medium text-gray-900 text-sm sm:text-base">
            {monitor.response_time ? `${Math.round(monitor.response_time * 1000)}ms` : '-'}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-gray-500 text-xs sm:text-sm">Interval</p>
          <p className="font-medium text-gray-900 text-sm sm:text-base">{monitor.check_interval / 60}min</p>
        </div>
      </div>

      {/* Enhanced Status Information */}
      {!monitor.is_up && monitor.status === 'active' && monitor.latest_incident && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-red-800">Current Issue</span>
          </div>
          
          {monitor.latest_incident.details?.user_impact && (
            <div className="mb-2">
              <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs ${getImpactColor(monitor.latest_incident.details.user_impact)}`}>
                {React.createElement(getImpactIcon(monitor.latest_incident.details.user_impact), {
                  className: 'w-3 h-3'
                })}
                <span>Impact: {monitor.latest_incident.details.user_impact}</span>
              </div>
            </div>
          )}
          
          {monitor.latest_incident.details?.impact_description && (
            <p className="text-sm text-red-700 mb-1">
              {monitor.latest_incident.details.impact_description}
            </p>
          )}
          
          {monitor.latest_incident.details?.status_code && (
            <p className="text-xs text-red-600">
              Error: HTTP {monitor.latest_incident.details.status_code}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        <div>
          Last checked: {monitor.last_checked ? (
            <span title={formatDateInUserTimezone(monitor.last_checked)}>
              {formatRelativeTime(monitor.last_checked)}
            </span>
          ) : 'Never'}
        </div>
        {monitor.last_checked && (
          <div className="text-xs text-gray-400 mt-1">
            {formatDateInUserTimezone(monitor.last_checked, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(MonitorCard);
